import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  accountingApi,
  formatInr,
  moneyNum,
  type AccountingVoucher,
} from "@/api/accounting";
import { useAccountingHospitalContext } from "./AccountingHospitalContext";

function statusVariant(status: string) {
  if (status === "POSTED") return "default" as const;
  if (status === "DRAFT") return "secondary" as const;
  if (status === "REVERSED" || status === "CANCELLED") return "outline" as const;
  return "secondary" as const;
}

export default function AccountingVouchersPage() {
  const { hospitalId, canPost } = useAccountingHospitalContext();

  const { data: vouchers = [], isLoading } = useQuery({
    queryKey: ["accounting-vouchers", hospitalId],
    queryFn: () => accountingApi.listVouchers(hospitalId, { take: 100 }),
    enabled: !!hospitalId,
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-medium">Vouchers</h2>
        {canPost ? (
          <Button asChild size="sm">
            <Link to="new">New journal</Link>
          </Button>
        ) : null}
      </div>
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Debit</TableHead>
              <TableHead>Narration</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6}>Loading…</TableCell>
              </TableRow>
            ) : vouchers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-slate-500">
                  No vouchers yet.
                </TableCell>
              </TableRow>
            ) : (
              (vouchers as AccountingVoucher[]).map((v) => (
                <TableRow key={v.id}>
                  <TableCell>
                    <Link
                      className="font-medium text-slate-900 underline-offset-2 hover:underline"
                      to={v.id}
                    >
                      {v.voucherNumber}
                    </Link>
                  </TableCell>
                  <TableCell>{String(v.voucherDate).slice(0, 10)}</TableCell>
                  <TableCell>{v.voucherType}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(v.status)}>{v.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {formatInr(moneyNum(v.totalDebit))}
                  </TableCell>
                  <TableCell className="max-w-[240px] truncate text-slate-600">
                    {v.narration || "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
